import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import {
    resetOperation, setOperationData,
    setCommandMessage,
} from '../../../redux/operationSlice';
import { updateGroundingById } from '../../../redux/groundingSlice'
import { createSnapMarker } from '../../../shared/snap/createSnapMarker'; 

import { getSnappedPoint } from '../../../shared/snap/getSnappedPoint';
import { createGroundingStrake } from '../../../factory/symbols/createGroundingStrake';
import { groundingBuat, createTwoLineLabel } from '../../../factory/symbols/createGroundingStrake';
import { getPerpendicularIntersection, isAxisAligned, getPreviewRayIntersection, getLineDirection, getDirectionalIntersection } from '../../../shared/geometry/getPrependicularIntersection';
import { applyGridSnap, getOrthoSnappedPoint } from "../../../modules/drawing";
import useDraggableLabels from './useGroundingDraggableLabels';
import { createTextLabel } from '../../../factory/symbols/createGroundingStrake';// metin için sprite üreticisi
import { getMidpointSnaps } from '../utils/getMidpointSnaps';
 
function addLabelForLongStrakeSegment(scene, start, end, threshold = 200) {
    const distance = start.distanceTo(end);
    if (distance < threshold) return;

    // Orta noktayı hesapla
    const midPoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);

    // Açıyı hesapla (radyan cinsinden)
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    let angle = Math.atan2(dy, dx);

    // Dereceye çevir
    let angleDeg = THREE.MathUtils.radToDeg(angle);

    // 90 ile 270 derece arasında ise 180 derece çıkar
    if (angleDeg > 90 && angleDeg < 270) {
        angleDeg -= 180;
    } else if (angleDeg < -90 && angleDeg > -270) {
        angleDeg += 180;
    }

    // Tekrar radyana çevir
    angle = THREE.MathUtils.degToRad(angleDeg);

    // Yazıyı oluştur
    const label = createTextLabel("Galvaniz topraklama şeridi", midPoint, angle);

    // Etiketin türünü ve bağlı olduğu segmenti sakla
    label.userData = {
        type: 'groundingLabel',
        lockedToSegment: [start.clone(), end.clone()]
    };

    // Sahneye ekle
    scene.add(label);
}

 
const useGroundingWiring = (scene, camera, renderer, snapPoints, options = {}) => {
    useDraggableLabels(scene, camera, renderer);
    const {
        enabled = () => { },
        onWireEnd = () => { },
        onWireStart = () => { },
    } = options;

    const strakeCount = useRef(0);
    const strakeLength = useRef(0);
    const groundingArea = useRef([]);
    const groundJoingtionCount = useRef(0);
    const startRef = useRef(false);
    const verticesRef = useRef([]);
    const tempLineRef = useRef(null);
    const finalPointRef = useRef(null);
    const snapMarkerRef = useRef(null);
    const snapSourceRef = useRef(null);
    const previewSymbolRef = useRef(null);
    const previewMarkersRef = useRef([]); // [{ startIdx, line }]
    const offsetLineRef = useRef(null); // SnapWall kopya çizgisi
    const intersectedLineRef = useRef(null); // Mavi olacak çizgi
    const lastMouse = useRef({ x: 0, y: 0 });
    const previewEndPointRef = useRef({ x: 0, y: 0 });
    const socketCountRef = useRef(0);
    const socketTypeIndex = useRef(0);
    const socketTypeRef = useRef('normal');
    const socketsRef = useRef([])
    const flipDirectionRef = useRef(1);
    const ctrlPressedRef = useRef(false);
    const lastOffsetDirectionRef = useRef(null); // ✅ Son açılı offset çizgisinin yönü

    const [step, setStep] = useState(0); // 0: ilk nokta, 1+: devam
    const [flipDir, setFlipDir] = useState(1);

    const { commandType } = useSelector((state) => state.operation);
    const gridMode = useSelector((state) => state.mods.gridMode);
    const objectSnap = useSelector((state) => state.mods.objectSnap);
    const operationData = useSelector((state) => state.operation.data);
    const snapMode = useSelector((state) => state.mods.snapMode);
    const orthoMode = useSelector((state) => state.mods.orthoMode); // Ortho modu Redux'tan

    const socketTypes = ['normal'];
    const snapWall = true;
    const wireColor = 0x0045ff
    const snapState = {
        x: 0, // 0: İlk adım, 1: Tam hizalama, 2: Üçüncü adım (5 birim kayma)
        y: 0, // Aynı şekilde Y için de
    };

    const dispatch = useDispatch(); 

    const setFlipDirection = (valOrUpdater) => {
        const newValue = typeof valOrUpdater === 'function' ? valOrUpdater(flipDirectionRef.current) : valOrUpdater;
        flipDirectionRef.current = newValue;
        setFlipDir(newValue);
    };



    // YENİ: Köşeye yakınlığı kontrol eden fonksiyon (istekte belirtildiği gibi).
    const findClosingVertexIndex = (point, vertices, threshold = 10) => {
        // Son köşe hariç diğerlerini kontrol et, kendine kapanmasın.
        for (let i = 0; i < vertices.length - 1; i++) {
            if (point.distanceTo(vertices[i]) <= threshold) {
                return i; // Yakın olan köşenin index'ini döndür
            }
        }
        return -1; // Yakın köşe bulunamadı
    };


    useEffect(() => {
        if (
            step === 0 &&
            operationData?.initialPoint &&
            enabled() &&
            verticesRef.current.length === 0
        ) {
            const point = new THREE.Vector3(
                operationData.initialPoint.x,
                operationData.initialPoint.y,
                0
            );


            // Snap etkisi taklit ediliyor
            verticesRef.current.push(point);
            startRef.current = true;

            dispatch(setCommandMessage("Sonraki noktaları seçin. Bitirmek için Enter/Space, iptal için ESC."));
            setStep(1);
        }
    }, [operationData, step, enabled]);

    useEffect(() => {
        if (!enabled() || !scene || !camera || !renderer) return;

        const domElement = renderer.domElement;
        dispatch(setCommandMessage("İlk noktayı seçin"));

        /**
         * İki kenarın açı ortayını hesaplar
         * @param {THREE.Vector3} prev - Önceki nokta
         * @param {THREE.Vector3} current - Mevcut nokta (kazık pozisyonu)
         * @param {THREE.Vector3} next - Sonraki nokta
         * @returns {number} Açı ortayı (radyan)
         */
        const calculateBisectorAngle = (prev, current, next) => {
            // Önceki kenarın yönü (current -> prev, ters yön)
            const dirToPrev = new THREE.Vector3().subVectors(prev, current).normalize();
            // Sonraki kenarın yönü (current -> next)
            const dirToNext = new THREE.Vector3().subVectors(next, current).normalize();

            // Açı ortayı = iki birim vektörün toplamının yönü
            const bisector = new THREE.Vector3().addVectors(dirToPrev, dirToNext).normalize();

            // Eğer vektörler paralel ise (180 derece), dik yönü kullan
            if (bisector.length() < 0.001) {
                // Dik yön (90 derece döndür) - dış bükey yön için +PI
                return Math.atan2(dirToPrev.x, -dirToPrev.y) + Math.PI;
            }

            // Dış bükey yön için açıyı 180 derece çevir
            return Math.atan2(bisector.y, bisector.x) + Math.PI;
        };

        /**
         * Tüm kazıkları açı ortaylarına göre hizalar
         * @param {boolean} isClosed - Polygon kapalı mı
         */
        const alignStakesToBisectors = (isClosed = false) => {
            const vertices = verticesRef.current;
            const sockets = socketsRef.current;

            if (vertices.length < 2 || sockets.length === 0) return;

            sockets.forEach((socketData, socketIndex) => {
                // Bu soketin vertex index'ini bul
                const vertexIndex = vertices.findIndex(v =>
                    v.distanceTo(socketData.position) < 1
                );

                if (vertexIndex === -1) return;

                let bisectorAngle;

                // İlk vertex
                if (vertexIndex === 0) {
                    if (isClosed && vertices.length > 2) {
                        // Kapalı polygon - son vertex'i önceki olarak kullan
                        const prev = vertices[vertices.length - 2]; // Son-1 (son vertex = ilk vertex)
                        const next = vertices[1];
                        bisectorAngle = calculateBisectorAngle(prev, vertices[0], next);
                    } else {
                        // Açık çizgi - sadece sonraki yöne bak
                        const direction = new THREE.Vector3().subVectors(vertices[1], vertices[0]);
                        bisectorAngle = Math.atan2(direction.y, direction.x) + Math.PI / 2;
                    }
                }
                // Son vertex
                else if (vertexIndex === vertices.length - 1) {
                    if (isClosed) {
                        // Kapalı polygon - zaten ilk vertex'e eşit
                        return;
                    } else {
                        // Açık çizgi - sadece önceki yöne bak
                        const direction = new THREE.Vector3().subVectors(vertices[vertexIndex], vertices[vertexIndex - 1]);
                        bisectorAngle = Math.atan2(direction.y, direction.x) + Math.PI / 2;
                    }
                }
                // Ara vertex
                else {
                    const prev = vertices[vertexIndex - 1];
                    const current = vertices[vertexIndex];
                    const next = vertices[vertexIndex + 1];
                    bisectorAngle = calculateBisectorAngle(prev, current, next);
                }

                // Sahne'deki stake objesini bul ve döndür
                scene.traverse((child) => {
                    if (child.uuid === socketData.uuid && child.userData?.type === 'groundingStrake') {
                        // offsetGroup'u döndür (children[0])
                        if (child.children[0]) {
                            child.children[0].rotation.z = bisectorAngle - Math.PI / 2;
                        }
                    }
                });
            });
        };

        // YENİ: Kod tekrarını önlemek için 'Enter' tuşu ve otomatik kapanma mantığını içeren fonksiyon
        // closingIndex: Hangi vertex'e kapandığını belirtir (-1 = kapanmadı, 0+ = kapanan vertex index)
        const completeWiring = (closingIndex = -1) => {
            setStep(0);
            if (!socketsRef.current.length) {
                addSocket();
            }
            let area = 0;
            let totalLength = 0;
            if (verticesRef.current.length > 1) {
                // Toplam şerit uzunluğunu hesapla (tüm segmentler)
                for (let i = 0; i < verticesRef.current.length - 1; i++) {
                    totalLength += verticesRef.current[i].distanceTo(verticesRef.current[i + 1]);
                }

                // Alan hesabı - Shoelace formülü
                // Sadece kapalı polygon oluşturulduysa alan hesapla
                if (closingIndex >= 0) {
                    // Kapalı polygon: closingIndex'ten son vertex'e kadar olan vertices
                    // Örn: vertices = [A, B, C, D, E, C] ve closingIndex = 2 (C)
                    // Polygon: C → D → E → C (index 2'den n-1'e)
                    const polygonVertices = verticesRef.current.slice(closingIndex);

                    if (polygonVertices.length >= 3) {
                        // Shoelace formülü ile alan hesapla
                        for (let i = 0; i < polygonVertices.length; i++) {
                            const j = (i + 1) % polygonVertices.length;
                            area += polygonVertices[i].x * polygonVertices[j].y;
                            area -= polygonVertices[j].x * polygonVertices[i].y;
                        }
                        area = Math.abs(area) / 20000; // cm² -> m² dönüşümü (100x100 = 10000, /2 = 20000)
                    }
                }

                strakeLength.current += totalLength / 100; // cm -> m dönüşümü
                if (area > 0) {
                    groundingArea.current.push(area);
                }
      
                // Kalıcı çizgiyi oluştur
                const geometry = new THREE.BufferGeometry().setFromPoints(verticesRef.current);
                const material = new THREE.LineBasicMaterial({ color: wireColor }); // Kalıcı çizgi rengi
                const permanentLine = new THREE.Line(geometry, material);

                permanentLine.uuid = tempLineRef.current?.uuid ?? THREE.MathUtils.generateUUID();
                permanentLine.userData = {
                    type: 'groundingStrip',
                    sockets: socketsRef.current.map(s => s.uuid),
                };

                scene.add(permanentLine);

                // ✅ Kazıkları açı ortaylarına hizala
                const isClosed = closingIndex >= 0;
                alignStakesToBisectors(isClosed);

                // onWireEnd'e bildir
                onWireEnd(verticesRef.current, socketsRef.current, snapSourceRef.current, permanentLine.uuid);
            }

            if (previewSymbolRef.current) {
                scene.remove(previewSymbolRef.current);
                previewSymbolRef.current.traverse((child) => {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) child.material.dispose();
                });
                previewSymbolRef.current = null;
            }

            if (tempLineRef.current) {
                scene.remove(tempLineRef.current);
                tempLineRef.current.geometry.dispose();
                tempLineRef.current.material.dispose();
                tempLineRef.current = null;
            }

            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                snapMarkerRef.current.geometry.dispose();
                snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }

            socketsRef.current = [];
            verticesRef.current = [];
            startRef.current = false;
            snapSourceRef.current = null;
            //--------------- grounding reducer---------

            dispatch(updateGroundingById({
  id: 'main_grounding', // Ana topraklama ID'si
  changes: {
    area: Math.max(...groundingArea.current),          // m²
    strakeCount: strakeCount.current,     // adet
    stripLength: strakeLength.current   ,  // metre
    joingtionCount: groundJoingtionCount.current
  }
}));
            //-----------------------
            // Yeni: tekrar başlatmayı önlemek için step state'i dışında operationData'yı da sıfırla
            dispatch(resetOperation());
            setStep(-1);
        };


        const handleClick = (event) => {
            if (!enabled()) return;

            const rect = domElement.getBoundingClientRect();
            const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
            lastMouse.current = { x, y };

            const mouseNDC = new THREE.Vector2(x, y);
            const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

            let dynamicSnapPoints = [...snapPoints];

            let finalPoint = worldPoint;
            let snapped = false;
            let snapSource = null;

            if (objectSnap && snapMode) {
                ({ finalPoint, snapped, snapSource } = getSnappedPoint(mouseNDC, worldPoint, dynamicSnapPoints, camera, renderer, { snapMode }));
            }

            if (!startRef.current && step === 0) {
                startRef.current = true;
                
                //----------------------
                const buat = new THREE.Mesh(
                    new THREE.CircleGeometry(4, 32),
                    new THREE.MeshBasicMaterial({ color: 0x0045ff, side: THREE.DoubleSide })
                );
                buat.position.set(finalPoint.x, finalPoint.y, 0.2);
                buat.userData = {
                    type: 'buatCircle',
                    socketUUID: "uuid",
                    isDraggable: false,
                };
                const textSprite = createTwoLineLabel('Bağlantı Filizi', '');
                textSprite.position.set(finalPoint.x + 0, finalPoint.y + 0, 0.3); // Başlangıç offset
                textSprite.userData = {
                    type: 'groundingBuatLabel',
                    parentBuat: buat,
                    offset: new THREE.Vector3(10, 30, 0),
                };
                scene.add(textSprite);
                //-----------------------------

                setStep(1);
                dispatch(setCommandMessage("Sonraki noktaları seçin. Bitirmek için Enter/Space, iptal için ESC."));
            }

            if (!snapped) {
                if (gridMode) {
                    finalPoint = applyGridSnap(worldPoint);
                } else {
                    finalPoint = worldPoint;
                }
            }
            if (snapWall && offsetLineRef.current && verticesRef.current.length > 0) {
                const targetLine = offsetLineRef.current;
                const positions = targetLine.geometry.attributes.position.array;
                const start = new THREE.Vector3(positions[0], positions[1], positions[2]);
                const end = new THREE.Vector3(positions[3], positions[4], positions[5]);
                const lastVertex = verticesRef.current[verticesRef.current.length - 1];
                
                // Çizgi eksen hizalı mı kontrol et
                if (isAxisAligned(start, end)) {
                    // Eksen hizalı çizgiler için
                    if (lastOffsetDirectionRef.current) {
                        // Önceki açılı çizginin yönünde devam et
                        finalPoint = getDirectionalIntersection(lastVertex, lastOffsetDirectionRef.current, { start, end });
                    } else {
                        // Normal perpendicular intersection kullan
                        finalPoint = getPerpendicularIntersection(lastVertex, { start, end });
                    }
                    // Eksen hizalı çizgiye tıklandığında açılı yön sıfırla
                    lastOffsetDirectionRef.current = null;
                } else {
                    // Açılı çizgiler için
                    if (lastOffsetDirectionRef.current) {
                        // Önceki açılı çizginin yönünde devam et
                        finalPoint = getDirectionalIntersection(lastVertex, lastOffsetDirectionRef.current, { start, end });
                    } else {
                        // Mouse yönünde kesişim hesapla
                        finalPoint = getPreviewRayIntersection(lastVertex, worldPoint, { start, end });
                    }
                    // Açılı çizginin yönünü sakla (sonraki çizim için)
                    lastOffsetDirectionRef.current = getLineDirection(start, end);
                }
            } else if (!snapped) {
                finalPoint = gridMode ? applyGridSnap(worldPoint) : worldPoint;
            }

            if (orthoMode && verticesRef.current.length > 0) {
                const lastVertex = verticesRef.current[verticesRef.current.length - 1];
                finalPoint = getOrthoSnappedPoint(lastVertex, finalPoint);
            }

            // YENİ: Otomatik Kapanma Kontrolü
            // En az 2 köşe varsa (bir çizgi oluşmuşsa) kapanma kontrolü yap.
            if (verticesRef.current.length >= 2) {
                const closingIndex = findClosingVertexIndex(finalPoint, verticesRef.current, 10); // 10 birim eşik değeri
                if (closingIndex !== -1) {
                    // Yakın bir köşe bulundu.
                    // 1. Son noktayı bulunan köşeye kenetle.
                    const closingVertex = verticesRef.current[closingIndex];
                    finalPoint.copy(closingVertex);

                    // 2. Kenetlenen noktayı köşe listesine ekle.
                    verticesRef.current.push(finalPoint);

                    const prevPoint = verticesRef.current.at(-2);
                    const closingDistance = prevPoint.distanceTo(finalPoint);

                    // 3. Çizim işlemini tamamla (Enter'a basılmış gibi).
                    // Sadece kapanma noktası son vertex değilse groundingBuat ekle
                    if (closingIndex !== 0) {
                        const p2 = finalPoint.clone();
                        const uuid = THREE.MathUtils.generateUUID();
                        groundingBuat(scene, p2, 0x0045ff, uuid);
                        groundJoingtionCount.current++
                    }
                    // Kapanma segmenti 200 birimden uzunsa etiket ekle
                    if (closingDistance > 200) {
                        addLabelForLongStrakeSegment(scene, prevPoint, finalPoint, 200);
                    }
                    // Çizim işlemini tamamla - closingIndex'i geçir (alan hesabı için)
                    completeWiring(closingIndex);

                    // 4. handleClick fonksiyonunun geri kalanının çalışmasını engelle.
                    return;
                }
            }


            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                snapMarkerRef.current.geometry.dispose();
                snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }

            if (snapped && snapSource) {
                const marker = createSnapMarker(8, snapSource);
                marker.position.copy(finalPoint);
                scene.add(marker);
                snapMarkerRef.current = marker;
            }

            verticesRef.current.push(finalPoint);
            if (verticesRef.current.length === 1 && onWireStart) {
                onWireStart(finalPoint);
            }
            if (verticesRef.current.length > 1) {
                const prev = verticesRef.current.at(-2);
                const curr = verticesRef.current.at(-1);
                addLabelForLongStrakeSegment(scene, prev, curr, 200);
            }
            if (verticesRef.current.length > 1) {
                const geometry = new THREE.BufferGeometry().setFromPoints(verticesRef.current);
                const material = new THREE.LineBasicMaterial({ color: 0xffff00 });

                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                }

                tempLineRef.current = new THREE.Line(geometry, material);
                scene.add(tempLineRef.current);
            }
            finalPointRef.current = finalPoint
            dispatch(setCommandMessage("Sonraki noktaları seçin. Bitirmek için Enter/Space, iptal için ESC."));
        };

const handleMouseMove = (event) => {
    if (!enabled()) return;

    const rect = domElement.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    lastMouse.current = { x, y };
    const mouseNDC = new THREE.Vector2(x, y);
    const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

    // -------- Bağlantı klemensi ön izlemesi --------
    
    // -----------------------------------------------

    // --- senin mevcut snap/preview kodların buradan devam ediyor ---
    let dynamicSnapPoints = [...snapPoints];
    const { finalPoint, snapped, snapSource } = getSnappedPoint(
        mouseNDC,
        worldPoint,
        dynamicSnapPoints,
        camera,
        renderer,
        { snapMode: snapMode }
    );

    if (snapMarkerRef.current) {
        scene.remove(snapMarkerRef.current);
        snapMarkerRef.current.geometry.dispose();
        snapMarkerRef.current.material.dispose();
        snapMarkerRef.current = null;
    }

    if (snapped && snapSource) {
        const marker = createSnapMarker(8, snapSource);
        marker.position.copy(snapSource.position);
        scene.add(marker);
        snapMarkerRef.current = marker;
    }

    let previewPoint = finalPoint;
    if (orthoMode && verticesRef.current.length > 0) {
        const lastVertex = verticesRef.current[verticesRef.current.length - 1];
        previewPoint = getOrthoSnappedPoint(lastVertex, finalPoint);
    }

    if (verticesRef.current.length > 0) {
        const previewPoints = [...verticesRef.current, previewPoint];
        const geometry = new THREE.BufferGeometry().setFromPoints(previewPoints);
        const material = new THREE.LineBasicMaterial({ color: 0xffff00 });

        if (tempLineRef.current) {
            scene.remove(tempLineRef.current);
            tempLineRef.current.geometry.dispose();
            tempLineRef.current.material.dispose();
        }

        tempLineRef.current = new THREE.Line(geometry, material);
        scene.add(tempLineRef.current);
        if (previewPoint instanceof THREE.Vector3) {
            previewEndPointRef.current = previewPoint.clone();
        }
    }

    if (previewSymbolRef.current) {
        scene.remove(previewSymbolRef.current);
        previewSymbolRef.current.traverse(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
        });
        previewSymbolRef.current = null;
    }

    if (verticesRef.current.length > 0 && previewPoint) {
        const lastPoint = verticesRef.current[verticesRef.current.length - 1];
        const directionVector = new THREE.Vector3().subVectors(previewPoint, lastPoint).normalize();
        const angle = Math.atan2(directionVector.y, directionVector.x);

        const symbolPreview = createGroundingStrake(
            null,
            previewPoint,
            flipDirectionRef.current,
            wireColor,
            true,
            socketTypeRef.current,
            null,
            0
        );

        symbolPreview.children[0].rotation.z = angle;
        scene.add(symbolPreview);
        previewSymbolRef.current = symbolPreview;
    }
};



        const addSocket = () => {
            if (!(previewEndPointRef.current instanceof THREE.Vector3)) {
                return;
            }

            verticesRef.current.push(previewEndPointRef.current.clone());

            const p1 = verticesRef.current.at(-2);
            const p2 = verticesRef.current.at(-1);
            const direction = new THREE.Vector3().subVectors(p2, p1);
            const angle = Math.atan2(direction.y, direction.x);

            // Yeni soket verisi
            const socketData = {
                position: p2.clone(),
                type: socketTypeRef.current,
                buat: null,
                direction,

            };
            socketsRef.current.push(socketData);

            // Topraklama kazığı (strake) oluşturuluyor
            const socket = createGroundingStrake(
                scene,
                p2.clone(),
                flipDirectionRef.current,
                wireColor,
                true,
                socketsRef.current,
                tempLineRef.current.uuid,
                0
            );
            socket.children[0].rotation.z = angle;

            // UUID kaydet
            const uuid = socket.uuid;
            socketsRef.current[socketsRef.current.length - 1].uuid = uuid;
            strakeCount.current++
            groundJoingtionCount.current++
            scene.add(socket);

            // ✅ Bu noktada yuvarlak buat'ı ekliyoruz
            groundingBuat(scene, p2.clone(), 0x0045ff, uuid);


            if (verticesRef.current.length > 1) {
                const prev = verticesRef.current.at(-2);
                const curr = verticesRef.current.at(-1);
                addLabelForLongStrakeSegment(scene, prev, curr, 200);
            }

            socketCountRef.current++;
            dispatch(resetOperation());
            dispatch(setOperationData({ startPoint: { x: 0, y: 0, z: 0 } }));
        };

        const handleKeyDown = (e) => {
            if (!enabled()) return;
            const movementOffset = 5; // Ofset miktarı (5 birim)

            if (snapMarkerRef.current) {
                const snapPos = snapMarkerRef.current.position;
                // Önceki pozisyonu saklayalım
                const previousPos = verticesRef.current[verticesRef.current.length - 1];
                if (!previousPos) return;
                let newPos = new THREE.Vector3(previousPos.x, previousPos.y, 0); // Başlangıç noktasını alıyoruz

                // Snap'ın sağda olup olmadığını kontrol et
                const isSnapRight = (snapPos.x >= previousPos.x)
                const isSnapLeft = (snapPos.x <= previousPos.x)
                const isSnapUp = (snapPos.y >= previousPos.y)
                const isSnapDown = (snapPos.y <= previousPos.y)
                // Aşağıda, yukarıda, solda ve sağda aktif olacak tuşları kontrol et
                if (e.key === 'a' && isSnapLeft) { // Sola hareket
                    snapState.y = 0;
                    if (!snapState.x) {
                        newPos.x = snapPos.x + movementOffset;
                        snapState.x = 1;
                    } else if (snapState.x === 1) {
                        newPos.x = snapPos.x;
                        snapState.x = 2;
                    } else if (snapState.x === 2) {
                        newPos.x = snapPos.x - movementOffset;
                        snapState.x = 0;
                    }
                } else if (e.key === 'd' && isSnapRight) { // Sağa hareket
                    snapState.y = 0;
                    if (!snapState.x) {
                        newPos.x = snapPos.x - movementOffset;
                        snapState.x = 1;
                    } else if (snapState.x === 1) {
                        newPos.x = snapPos.x;
                        snapState.x = 2;
                    } else if (snapState.x === 2) {
                        newPos.x = snapPos.x + movementOffset;
                        snapState.x = 0;
                    }
                } else if (e.key === 'w' && isSnapUp) { // Yukarı hareket
                    snapState.x = 0;
                    if (!snapState.y) {
                        newPos.y = snapPos.y - movementOffset;
                        snapState.y = 1;
                    } else if (snapState.y === 1) {
                        newPos.y = snapPos.y;
                        snapState.y = 2;
                    } else if (snapState.y === 2) {
                        newPos.y = snapPos.y + movementOffset;
                        snapState.y = 0;
                    }
                } else if (e.key === 's' && isSnapDown) { // Aşağı hareket
                    snapState.x = 0;
                    if (!snapState.y) {
                        newPos.y = snapPos.y + movementOffset;
                        snapState.y = 1;
                    } else if (snapState.y === 1) {
                        newPos.y = snapPos.y;
                        snapState.y = 2;
                    } else if (snapState.y === 2) {
                        newPos.y = snapPos.y - movementOffset;
                        snapState.y = 0;
                    }
                }

                // Yeni pozisyonu ekleyelim ve geçici çizgiyi güncelleyelim
                if (newPos !== previousPos) {
                    verticesRef.current.push(newPos);
                    if (tempLineRef.current) {
                        scene.remove(tempLineRef.current);
                        tempLineRef.current.geometry.dispose();
                        tempLineRef.current.material.dispose();
                    }

                    const previewPoints = [...verticesRef.current];
                    const geometry = new THREE.BufferGeometry().setFromPoints(previewPoints);
                    const material = new THREE.LineBasicMaterial({ color: 0xffff00 });
                    tempLineRef.current = new THREE.Line(geometry, material);
                    scene.add(tempLineRef.current);
                }
            }
            if (e.key === 't') {
                socketTypeIndex.current = (socketTypeIndex.current + 1) % socketTypes.length;
                socketTypeRef.current = socketTypes[socketTypeIndex.current];
                //  dispatch(setCommandMessage(`Priz tipi: ${socketTypeRef.current}`));
                return;
            }
            if (e.key === 'q') {
                // Mouse pozisyonunu world koordinatlarına dönüştür
                // Çizgiye ilk 5 birimi ekle
                const previousPos = verticesRef.current[verticesRef.current.length - 1];

                const direction = new THREE.Vector3(
                    previewEndPointRef.current.x - previousPos.x,
                    previewEndPointRef.current.y - previousPos.y,
                    0
                ).normalize(); // Yönü normalize et

                // Yeni pozisyonu bu yöne göre hesapla
                let newPos = new THREE.Vector3(
                    previousPos.x + direction.x * movementOffset,
                    previousPos.y + direction.y * movementOffset,
                    0
                );

                // Yeni pozisyonu ekleyelim ve geçici çizgiyi güncelleyelim
                verticesRef.current.push(newPos);
                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                }

                const previewPoints = [...verticesRef.current];
                const geometry = new THREE.BufferGeometry().setFromPoints(previewPoints);
                const material = new THREE.LineBasicMaterial({ color: 0xffff00 });
                tempLineRef.current = new THREE.Line(geometry, material);
                scene.add(tempLineRef.current);

            }
            if (e.key === 'e') {
                const previousPos = verticesRef.current[verticesRef.current.length - 1];
                if (!previousPos) return;

                // Mouse'un sahnedeki son konumunu hesapla
                const mouseNDC = new THREE.Vector2(lastMouse.current.x, lastMouse.current.y);
                const mouseWorld = new THREE.Vector3(lastMouse.current.x, lastMouse.current.y, 0).unproject(camera);

                // Mouse yönü ile son nokta arasındaki fark
                const deltaX = mouseWorld.x - previousPos.x;
                const deltaY = mouseWorld.y - previousPos.y;

                // 45 derece yönlerde ofset vektör
                const offsetLength = 7.07106781187; // toplam uzunluk
                const dx = offsetLength / Math.sqrt(2); // 3.5355
                const dy = dx;

                let offset = new THREE.Vector3();

                if (deltaX >= 0 && deltaY >= 0) {
                    // Sağ üst (45°)
                    offset.set(dx, dy, 0);
                } else if (deltaX < 0 && deltaY >= 0) {
                    // Sol üst (135°)
                    offset.set(-dx, dy, 0);
                } else if (deltaX < 0 && deltaY < 0) {
                    // Sol alt (225°)
                    offset.set(-dx, -dy, 0);
                } else {
                    // Sağ alt (315°)
                    offset.set(dx, -dy, 0);
                }

                const newPos = new THREE.Vector3().addVectors(previousPos, offset);
                verticesRef.current.push(newPos);

                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                }

                const previewPoints = [...verticesRef.current];
                const geometry = new THREE.BufferGeometry().setFromPoints(previewPoints);
                const material = new THREE.LineBasicMaterial({ color: 0xffff00 });
                tempLineRef.current = new THREE.Line(geometry, material);
                scene.add(tempLineRef.current);
            }
            const cancelWiring = () => {
                // Seçili groundingStrake'leri eski rengine döndür
                scene.traverse((child) => {
                    if (child.userData?.type === 'groundingStrake' && child.userData?.isSelected) {
                        child.userData.isSelected = false;
                        child.traverse((obj) => {
                            if (obj.isMesh || obj.type === 'Line') {
                                if (obj.material && obj.material.color) {
                                    const orig = obj.userData?.originalColor;
                                    if (orig !== undefined) {
                                        obj.material.color.set(orig);
                                    }
                                }
                            }
                        });
                    }
                });


                // Geçici çizgiyi temizle
                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                    tempLineRef.current = null;
                }

                // Ön izleme sembollerini temizle
                const toRemove = [];
                scene.traverse((child) => {
                    if (
                        (child.userData?.type === 'groundingStrake' && child.userData?.isPreview) ||
                        child.userData?.type === 'buatCircle' ||
                        child.userData?.type === 'groundingBuatLabel' ||
                        child.userData?.type === 'groundingLabel'
                    ) {
                        toRemove.push(child);
                    }
                });
                toRemove.forEach((child) => {
                    if (child.traverse && typeof child.traverse === 'function') {
                        child.traverse((obj) => {
                            if (obj.geometry) obj.geometry.dispose();
                            if (obj.material) obj.material.dispose();
                        });
                    }
                    scene.remove(child);
                });

                // Snap marker’ı temizle
                if (snapMarkerRef.current) {
                    scene.remove(snapMarkerRef.current);
                    snapMarkerRef.current.geometry.dispose();
                    snapMarkerRef.current.material.dispose();
                    snapMarkerRef.current = null;
                }

                // Ref ve state sıfırlama
                verticesRef.current = [];
                socketsRef.current = [];
                previewMarkersRef.current = [];
                previewSymbolRef.current = null;
                offsetLineRef.current = null;
                intersectedLineRef.current = null;
                snapSourceRef.current = null;
                finalPointRef.current = null;
                previewEndPointRef.current = { x: 0, y: 0 };

                startRef.current = false;
                setStep(0);

                dispatch(resetOperation());
                dispatch(setCommandMessage(""));
            };





            if (e.key === 'Escape') {
                if (!startRef.current) return; // işlem başlamadıysa ESC etkisiz

                cancelWiring();
                return;
            }

            if (e.key === ' ') {
                addSocket()
            }
            if (e.key === 'Enter') {
                // Enter ile tamamlama - kapalı alan yok (closingIndex = -1)
                // completeWiring tüm temizlik işlemlerini ve Redux güncellemesini yapar
                completeWiring(-1);
            }

            if (e.key === 'r') {
                setFlipDirection((prev) => -prev);
                return;
            }

        };

        domElement.addEventListener('click', handleClick);
        domElement.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keyup', handleKeyDown);

        return () => {
            domElement.removeEventListener('click', handleClick);
            domElement.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('keyup', handleKeyDown);
        };
    }, [commandType, scene, camera, renderer, gridMode, objectSnap, flipDir, snapPoints, dispatch]);
};

export default useGroundingWiring;
